import React, { useEffect, useMemo, useState } from "react";
import Toast from "./components/ui/Toast";
import SearchModal from "./components/ui/SearchModal";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import Hero from "./components/layout/Hero";
import MobileMenu from "./components/layout/MobileMenu";
import SizeGuideModal from "./components/ui/SizeGuideModal";
import ShippingInfoModal from "./components/ui/ShippingInfoModal";
import ReturnsModal from "./components/ui/ReturnsModal";
import ProductCard from "./components/product/ProductCard";
import ShopPage from "./components/product/ShopPage";
import ProductDetailPage from "./components/product/ProductDetailPage";
import Cart from "./components/cart/Cart";
import CheckoutPage from "./components/cart/CheckoutPage";
import WishlistPage from "./components/pages/WishlistPage";
import AboutPage from "./components/pages/AboutPage";
import ContactPage from "./components/pages/ContactPage";
import useProducts from "./data/useProducts";
import useStore from "./store/useStore";
import { parsePath, pathFor, productPath } from "./lib/routes";

const SHOP_DEFAULT_STATE = {
  selectedGender: "All",
  selectedCategory: "All",
  priceRange: [0, 5000],
  selectedMetal: "All",
  availability: "All",
  sortBy: "featured",
  currentPage: 1,
  itemsPerPage: 12,
};

const App = () => {
  // ============================================
  // ROUTING STATE (Managed in React)
  // ============================================
  // The page comes from the URL so shared links open where they point.
  const [initialRoute] = useState(() => parsePath(window.location.pathname));
  const [currentPage, setCurrentPage] = useState(initialRoute.page);
  const [productSku, setProductSku] = useState(initialRoute.sku);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [shippingInfoOpen, setShippingInfoOpen] = useState(false);
  const [returnsOpen, setReturnsOpen] = useState(false);
  const [shopState, setShopState] = useState(SHOP_DEFAULT_STATE);

  // ============================================
  // DATA FROM ZUSTAND STORE
  // ============================================
  const toast = useStore((state) => state.toast);
  const hideToast = useStore((state) => state.hideToast);

  // Catalog lives in PocketBase; a bundled snapshot renders instantly and
  // covers a cold start or outage.
  const { products, loading, stale } = useProducts();

  // Ensure featured section shows both women's and men's highlights
  const featuredProducts = useMemo(() => {
    const womenPicks = products
      .filter((product) => product.gender === "Women" && !product.outOfStock)
      .slice(0, 4);
    const menPicks = products
      .filter((product) => product.gender === "Men" && !product.outOfStock)
      .slice(0, 4);
    return [...womenPicks, ...menPicks];
  }, [products]);

  // Looked up by SKU so live data replacing the snapshot refreshes it too.
  const selectedProduct = useMemo(
    () => (productSku ? products.find((p) => p.sku === productSku) ?? null : null),
    [products, productSku]
  );

  // ============================================
  // NAVIGATION FUNCTIONS
  // ============================================
  const showPage = (page, product = null) => {
    setCurrentPage(page);
    setProductSku(product ? product.sku : null);
    const path = pathFor(page, product);
    if (window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
    window.scrollTo(0, 0);
  };

  // Browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const route = parsePath(window.location.pathname);
      setCurrentPage(route.page);
      setProductSku(route.sku);
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Product links: fall back to the collection if the product is gone, and
  // fix up the slug if the name changed since the link was shared.
  useEffect(() => {
    if (currentPage !== "product-detail") return;
    if (selectedProduct) {
      const path = productPath(selectedProduct);
      if (window.location.pathname !== path) {
        window.history.replaceState(null, "", path);
      }
    } else if (!loading) {
      window.history.replaceState(null, "", pathFor("shop"));
      setCurrentPage("shop");
      setProductSku(null);
    }
  }, [currentPage, selectedProduct, loading]);

  useEffect(() => {
    document.title =
      currentPage === "product-detail" && selectedProduct
        ? `${selectedProduct.name} | Maison de Bijoux`
        : "MAISON DE BIJOUX";
  }, [currentPage, selectedProduct]);

  const navigateTo = (page, options = {}) => {
    if (page === "shop") {
      setShopState((prev) => ({
        ...prev,
        ...(options.resetFilters ? SHOP_DEFAULT_STATE : {}),
        ...(options.category ? { selectedCategory: options.category } : {}),
        currentPage: options.page ?? 1,
      }));
    }
    showPage(page);
  };

  const handleViewProduct = (product) => showPage("product-detail", product);

  const handleBackToShop = () => showPage("shop");

  const handleCheckout = () => showPage("checkout");

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f3f9] to-[#e8e4f3]">
      {/* Toast Notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      {/* Header */}
      <Header currentPage={currentPage} onNavigate={navigateTo} />

      {stale && (
        <div className="px-4 py-2 text-sm text-center text-amber-900 bg-amber-50 border-b border-amber-200">
          Showing our last saved catalog — live prices and availability may
          differ.
        </div>
      )}

      {/* Modals */}
      <SearchModal products={products} onViewProduct={handleViewProduct} />
      <MobileMenu currentPage={currentPage} onNavigate={navigateTo} />
      <Cart onCheckout={handleCheckout} />
      <SizeGuideModal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
      <ShippingInfoModal
        open={shippingInfoOpen}
        onClose={() => setShippingInfoOpen(false)}
      />
      <ReturnsModal open={returnsOpen} onClose={() => setReturnsOpen(false)} />

      {/* Pages */}
      {currentPage === "home" && (
        <>
          <Hero onNavigate={navigateTo} />
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center mb-12 space-y-4">
              <h3 className="text-4xl font-serif text-gray-900">
                Featured Collection
              </h3>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                A curated peek at our women's and men's bestsellers. Want
                everything? Browse the full catalog below.
              </p>
              <button
                onClick={() => navigateTo("shop")}
                className="inline-flex items-center justify-center px-5 py-2 text-sm font-semibold text-white bg-gray-900 rounded-full hover:bg-gray-800 transition"
              >
                View all products
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {loading && featuredProducts.length === 0
                ? Array.from({ length: 8 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-white border border-gray-100 rounded-2xl overflow-hidden animate-pulse"
                    >
                      <div className="h-72 bg-gray-200" />
                      <div className="p-6 space-y-3">
                        <div className="w-1/3 h-3 bg-gray-200 rounded" />
                        <div className="w-2/3 h-4 bg-gray-200 rounded" />
                        <div className="w-1/2 h-6 bg-gray-200 rounded" />
                      </div>
                    </div>
                  ))
                : featuredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onViewDetails={handleViewProduct}
                    />
                  ))}
            </div>
          </section>
        </>
      )}

      {currentPage === "shop" && (
        <ShopPage
          products={products}
          onViewProduct={handleViewProduct}
          state={shopState}
          onStateChange={setShopState}
        />
      )}

      {currentPage === "wishlist" && (
        <WishlistPage onViewProduct={handleViewProduct} />
      )}

      {currentPage === "product-detail" && !selectedProduct && (
        <div className="py-32 text-center text-gray-500">Loading…</div>
      )}

      {currentPage === "product-detail" && selectedProduct && (
        <ProductDetailPage
          key={selectedProduct.sku}
          product={selectedProduct}
          onBack={handleBackToShop}
        />
      )}

      {currentPage === "checkout" && <CheckoutPage onNavigate={navigateTo} />}

      {currentPage === "about" && <AboutPage />}

      {currentPage === "contact" && <ContactPage />}

      {/* Footer */}
      <Footer
        onNavigate={navigateTo}
        onOpenSizeGuide={() => setSizeGuideOpen(true)}
        onOpenShippingInfo={() => setShippingInfoOpen(true)}
        onOpenReturns={() => setReturnsOpen(true)}
      />

      {/* Animations */}
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default App;
